/**
 * Navigation Registry — Maps each context to its sidebar nav items.
 *
 * When activeContext === 'all', the sidebar shows ALL items (current behavior).
 * When a specific context is selected, only that context's items + common items are shown.
 */

import {
  LayoutDashboard, ClipboardCheck, AlertTriangle, Shield,
  FileText, MessageSquare, Settings, Building2, Target,
  Library, Database, FolderOpen, BarChart3, Plug,
  Search as SearchIcon, Users, Layers, TrendingUp,
  Calculator, Map, ListOrdered, UserCog, Lock,
  Activity, Eye, FileCheck, Scale, Cpu, Boxes,
  Crown, ShieldCheck, Siren, Bug, KeyRound,
  RefreshCw, BrainCircuit, Gavel, Globe, CreditCard,
  Repeat2, Gauge, PanelsTopLeft, Wrench, Compass,
  Network, Milestone, Crosshair, LineChart, Waypoints,
  BookOpen, Radar, Heart, FolderKanban, Grid2x2,
  Sparkles, Calendar, Zap, ScanLine,
  Leaf, Cookie, Cloud, Gamepad2, CheckCircle, Award,
  Radio, Bot, ScanSearch,
  Newspaper, ArrowLeftRight, FileBarChart,
  Link2, PieChart, FlaskConical, Bell, SlidersHorizontal,
  TrendingDown, Brain, GitBranch, ClipboardList, Megaphone,
  MessagesSquare, FileSearch, Star, Trophy, ListChecks,
} from 'lucide-react'

import type { ContextId } from '@/types/real-estate'

export interface NavItem {
  title: string
  icon: React.ElementType
  href?: string
  children?: NavItem[]
  featureId?: string
  badge?: number | string
}

// ─── Common items (always shown) ─────────────────────────────────────────────

export const commonNavItems: NavItem[] = [
  { title: 'Tableau de bord', icon: LayoutDashboard, href: '/dashboard' },
  { title: 'Comparatif & Reporting', icon: BarChart3, href: '/analytics/comparatif' },
  { title: 'Import de données', icon: Database, href: '/import-hub' },
  { title: 'Régie Publicitaire', icon: Megaphone, href: '/ads' },
  {
    title: 'Mes Prestataires', icon: Building2,
    children: [
      { title: 'Tous mes prestataires', icon: Building2, href: '/prestataires' },
      { title: 'Offres reçues', icon: CreditCard, href: '/prestataires/offres' },
    ],
  },
  { title: 'Mes Dossiers', icon: FolderOpen, href: '/mes-dossiers' },
  { title: 'Messagerie', icon: MessageSquare, href: '/messages' },
  { title: 'Mes rendez-vous', icon: Calendar, href: '/booking' },
  { title: 'Forums', icon: MessagesSquare, href: '/forum' },
  {
    title: 'Consentements', icon: ShieldCheck,
    children: [
      { title: 'En attente', icon: ShieldCheck, href: '/consents/pending' },
      { title: 'Actifs', icon: FileCheck, href: '/consents/active' },
      { title: 'Historique', icon: FileText, href: '/consents/history' },
    ],
  },
  { title: 'Coffre-fort', icon: Lock, href: '/vault' },
  {
    title: 'Auto-Gestion', icon: UserCog,
    children: [
      { title: 'Crédits IA', icon: BrainCircuit, href: '/account/credits' },
      { title: 'Abonnement & Paiements', icon: CreditCard, href: '/self-manage/finances' },
    ],
  },
  {
    title: 'IA & Copilote', icon: BrainCircuit,
    children: [
      { title: 'Validations IA', icon: ShieldCheck, href: '/ai-approvals' },
      { title: 'Activité IA', icon: Activity, href: '/ai-activity' },
      { title: 'Graphe IA', icon: Network, href: '/ai-graph' },
    ],
  },
]

// ─── Core module items ───────────────────────────────────────────────────────

export const assessmentsNav: NavItem[] = [
  {
    title: 'Évaluations', icon: ClipboardCheck,
    children: [
      { title: 'Centre d\'évaluations', icon: ClipboardCheck, href: '/assessments' },
      { title: 'Créer une évaluation', icon: FileText, href: '/assessments/create' },
    ],
  },
]

export const risksNav: NavItem[] = [
  {
    title: 'Gestion des risques', icon: AlertTriangle,
    children: [
      { title: 'Registre des risques', icon: AlertTriangle, href: '/risks' },
      { title: 'Carte thermique', icon: Layers, href: '/risks/heatmap' },
      { title: 'Assistant d\'évaluation', icon: Target, href: '/risks/assessment' },
    ],
  },
]

export const complianceNav: NavItem[] = [
  {
    title: 'Conformité', icon: Shield,
    children: [
      { title: 'Vue d\'ensemble', icon: Shield, href: '/compliance' },
      { title: 'PIA (AIPD)', icon: Eye, href: '/compliance/pia' },
      { title: 'Demandes DSR', icon: Users, href: '/compliance/dsr' },
    ],
  },
]

export const frameworksNav: NavItem[] = [
  {
    title: 'Référentiels', icon: Library,
    children: [
      { title: 'Bibliothèque', icon: Library, href: '/frameworks' },
      { title: 'Contrôles', icon: FileCheck, href: '/controls' },
      { title: 'Statut contrôles', icon: Activity, href: '/controls/status' },
    ],
  },
]

export const governanceNav: NavItem[] = [
  {
    title: 'Gouvernance', icon: Scale,
    children: [
      { title: 'Politiques', icon: FileText, href: '/governance' },
      { title: 'Nouvelle politique', icon: FileText, href: '/governance/policies/new' },
    ],
  },
]

export const strategyNav: NavItem[] = [
  {
    title: 'Stratégie', icon: Target,
    children: [
      { title: 'Vision & Mission', icon: Target, href: '/strategy/vision' },
      { title: 'Objectifs', icon: TrendingUp, href: '/strategy/objectives' },
      { title: 'Business Case', icon: FileText, href: '/strategy/business-case' },
      { title: 'Calculateur ROI', icon: Calculator, href: '/strategy/roi-calculator' },
      { title: 'Roadmap', icon: Map, href: '/strategy/roadmap' },
      { title: 'Priorisation', icon: ListOrdered, href: '/strategy/prioritization' },
    ],
  },
]

export const maturityNav: NavItem[] = [
  {
    title: 'Maturité', icon: Cpu,
    children: [
      { title: 'Tableau de bord', icon: LayoutDashboard, href: '/maturity' },
      { title: 'Référentiels', icon: Library, href: '/maturity/frameworks' },
      { title: 'Évaluations', icon: ClipboardCheck, href: '/maturity/assessments' },
      { title: 'Modèles', icon: Boxes, href: '/maturity/templates' },
      { title: 'Paramètres', icon: Settings, href: '/maturity/settings' },
    ],
  },
]

export const evidenceNav: NavItem[] = [
  {
    title: 'Preuves', icon: FolderOpen,
    children: [
      { title: 'Depot de preuves', icon: FolderOpen, href: '/evidence' },
    ],
  },
]

// ─── PRO module items ────────────────────────────────────────────────────────

export const governanceProNav: NavItem[] = [
  {
    title: 'GovernancePRO', icon: Crown,
    children: [
      { title: 'Hub Gouvernance', icon: LayoutDashboard, href: '/governance-pro' },
      { title: 'Cockpit GRC', icon: BarChart3, href: '/governance-pro/cockpit' },
      { title: 'Score & Maturite', icon: Gauge, href: '/governance-pro/maturity-bridge' },
      { title: 'Cadre de Gouvernance', icon: Shield, href: '/governance-pro/wizard' },
      { title: 'Politiques', icon: FileText, href: '/governance-pro/policies' },
      { title: 'Directives', icon: BookOpen, href: '/governance-pro/directives' },
      { title: 'Procedures', icon: ListOrdered, href: '/governance-pro/procedures' },
      { title: 'Standards', icon: FileCheck, href: '/governance-pro/standards' },
      { title: 'RACI & Roles', icon: Users, href: '/governance-pro/roles' },
      { title: 'GRC & Conformite', icon: Scale, href: '/governance-pro/grc' },
      { title: 'Registre Risques', icon: AlertTriangle, href: '/governance-pro/risk-register' },
      { title: 'Controles & Audit', icon: Shield, href: '/governance-pro/controls-audit' },
      { title: 'Impact IA', icon: BrainCircuit, href: '/governance-pro/ai-impact' },
      { title: 'Analyse Strategique', icon: Target, href: '/governance-pro/strategic-analysis' },
      { title: 'Culture', icon: Globe, href: '/governance-pro/culture' },
      { title: 'Roadmap', icon: Map, href: '/governance-pro/roadmap' },
      { title: 'Objectifs', icon: Target, href: '/governance-pro/objectives' },
      { title: 'Processus', icon: Activity, href: '/governance-pro/processes' },
      { title: 'Programmes', icon: Layers, href: '/governance-pro/programs/data' },
      { title: 'Rapports', icon: BarChart3, href: '/governance-pro/reports' },
      { title: 'Audit Trail', icon: Activity, href: '/governance-pro/audit-trail' },
      { title: 'Integration', icon: Plug, href: '/governance-pro/integration' },
      { title: 'Settings', icon: Settings, href: '/governance-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/governance-pro/outils' },
    ],
  },
]

export const securityProNav: NavItem[] = [
  {
    title: 'SecurityPRO', icon: ShieldCheck,
    children: [
      { title: 'Hub Securite', icon: LayoutDashboard, href: '/security-pro' },
      { title: 'Cockpit NIST CSF', icon: Shield, href: '/security-pro/cockpit' },
      { title: 'Score Cyber', icon: Gauge, href: '/security-pro/score' },
      { title: 'Posture Securite', icon: Crosshair, href: '/security-pro/posture' },
      { title: 'SOC & Detection', icon: Siren, href: '/security-pro/soc' },
      { title: 'Alertes & SIEM', icon: AlertTriangle, href: '/security-pro/alerts' },
      { title: 'Incidents', icon: Activity, href: '/security-pro/incidents' },
      { title: 'Threat Intelligence', icon: Radar, href: '/security-pro/threat-intel' },
      { title: 'Vulnerabilites', icon: Bug, href: '/security-pro/vulns' },
      { title: 'Controles', icon: FileCheck, href: '/security-pro/controls' },
      { title: 'IAM & Zero Trust', icon: KeyRound, href: '/security-pro/iam' },
      { title: 'Revue des Accès (SoD)', icon: ScanLine, href: '/security-pro/access-review' },
      { title: 'Chiffrement', icon: Lock, href: '/security-pro/encryption' },
      { title: 'BCP/DRP', icon: RefreshCw, href: '/security-pro/bcp' },
      { title: 'Exercices & Tests', icon: Target, href: '/security-pro/exercises' },
      { title: 'Reprise Activite', icon: RefreshCw, href: '/security-pro/dr' },
      { title: 'Conformite', icon: Scale, href: '/security-pro/frameworks' },
      { title: 'ISO 27001', icon: Shield, href: '/security-pro/iso27001' },
      { title: 'SOC 2', icon: FileCheck, href: '/security-pro/soc2' },
      { title: 'Rapports', icon: BarChart3, href: '/security-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/security-pro/audit-trail' },
      { title: 'Integration', icon: Plug, href: '/security-pro/integration' },
      { title: 'Settings', icon: Settings, href: '/security-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/security-pro/outils' },
      { title: 'Copilot Posture Sécurité', icon: ShieldCheck, href: '/security-pro/security-posture-copilot' },
      { title: 'Scorecard Zero Trust', icon: CheckCircle, href: '/security-pro/zero-trust-scorecard' },
      { title: 'Threat Hunting', icon: ScanSearch, href: '/security-pro/threat-hunting' },
      { title: 'Surveillance continue (CCM)', icon: ScanLine, href: '/security-pro/ccm' },
      { title: 'Tests de controles (CCM)', icon: ClipboardCheck, href: '/security-pro/ccm/control-tests' },
    ],
  },
]

export const riskProNav: NavItem[] = [
  {
    title: 'RiskPRO', icon: AlertTriangle,
    children: [
      { title: 'Hub GRC & Risques', icon: LayoutDashboard, href: '/risk-pro' },
      { title: 'Cockpit Risques', icon: Target, href: '/risk-pro/cockpit' },
      { title: 'Appetit Risque', icon: Gauge, href: '/risk-pro/appetite' },
      { title: 'Matrice 5x5', icon: Layers, href: '/risk-pro/matrix' },
      { title: 'Registre', icon: FileText, href: '/risk-pro/register' },
      { title: 'Identification', icon: SearchIcon, href: '/risk-pro/identify' },
      { title: 'Scenarios', icon: Waypoints, href: '/risk-pro/scenarios' },
      { title: 'Taxonomie', icon: Layers, href: '/risk-pro/taxonomy' },
      { title: 'Evaluation', icon: ClipboardCheck, href: '/risk-pro/assessment' },
      { title: 'Bowtie', icon: Waypoints, href: '/risk-pro/bowtie' },
      { title: 'Quantitatif', icon: Calculator, href: '/risk-pro/quantitative' },
      { title: 'Simulation Monte-Carlo', icon: FlaskConical, href: '/risk-pro/montecarlo' },
      { title: 'Controles', icon: Shield, href: '/risk-pro/controls' },
      { title: 'Traitement', icon: Target, href: '/risk-pro/treatment' },
      { title: '3 Lignes Defense', icon: Layers, href: '/risk-pro/three-lines' },
      { title: 'Incidents', icon: Siren, href: '/risk-pro/incidents' },
      { title: 'KRI & Monitoring', icon: Activity, href: '/risk-pro/kri' },
      { title: 'Cadres', icon: Library, href: '/risk-pro/frameworks' },
      { title: 'Rapports CA', icon: BarChart3, href: '/risk-pro/report-board' },
      { title: 'Rapports Risque', icon: BarChart3, href: '/risk-pro/report-risk' },
      { title: 'Audit Trail', icon: Activity, href: '/risk-pro/audit-trail' },
      { title: 'Integration', icon: Plug, href: '/risk-pro/integration' },
      { title: 'Settings', icon: Settings, href: '/risk-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/risk-pro/outils' },
      { title: 'Moniteur Appétit Risque', icon: Gauge, href: '/risk-pro/risk-appetite-monitor' },
      { title: 'Analyse Événements Perte', icon: TrendingDown, href: '/risk-pro/loss-event-analysis' },
      { title: 'Surveillance continue (CCM)', icon: ScanLine, href: '/security-pro/ccm' },
    ],
  },
]

export const dataProNav: NavItem[] = [
  {
    title: 'DataPRO', icon: Database,
    children: [
      { title: 'Hub Donnees', icon: LayoutDashboard, href: '/data-pro' },
      { title: 'Cockpit DAMA', icon: BarChart3, href: '/data-pro/cockpit' },
      { title: 'Score Data', icon: Gauge, href: '/data-pro/score' },
      { title: 'Maturite', icon: TrendingUp, href: '/data-pro/maturite' },
      { title: 'Gouvernance', icon: Scale, href: '/data-pro/governance' },
      { title: 'Qualite', icon: Activity, href: '/data-pro/quality' },
      { title: 'MDM & Catalogue', icon: Boxes, href: '/data-pro/mdm' },
      { title: 'Modelisation', icon: Network, href: '/data-pro/modeling' },
      { title: 'Architecture', icon: Layers, href: '/data-pro/architecture' },
      { title: 'Metadata & Lineage', icon: Network, href: '/data-pro/metadata' },
      { title: 'DSPM (Posture Securite Data)', icon: ScanSearch, href: '/data-pro/dspm' },
      { title: 'Integration Donnees', icon: Plug, href: '/data-pro/integration' },
      { title: 'Stockage', icon: Database, href: '/data-pro/storage' },
      { title: 'DWH & BI', icon: BarChart3, href: '/data-pro/dwh-bi' },
      { title: 'Documents', icon: FileText, href: '/data-pro/documents' },
      { title: 'Cycle de Vie', icon: RefreshCw, href: '/data-pro/lifecycle' },
      { title: 'Securite', icon: Lock, href: '/data-pro/security' },
      { title: 'DCAM', icon: Shield, href: '/data-pro/dcam' },
      { title: 'CDMC', icon: Shield, href: '/data-pro/cdmc' },
      { title: 'Modeles Maturite', icon: TrendingUp, href: '/data-pro/maturity-models' },
      { title: 'Cadres', icon: Library, href: '/data-pro/frameworks' },
      { title: 'Rapports', icon: BarChart3, href: '/data-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/data-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/data-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/data-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/data-pro/outils' },
    ],
  },
]

export const complianceProNav: NavItem[] = [
  {
    title: 'CompliancePRO', icon: Gavel,
    children: [
      { title: 'Hub Compliance', icon: LayoutDashboard, href: '/compliance-pro' },
      { title: 'Cockpit Multi-Reg', icon: Shield, href: '/compliance-pro/cockpit' },
      { title: 'Score Conformite', icon: Gauge, href: '/compliance-pro/score' },
      { title: 'Posture', icon: Crosshair, href: '/compliance-pro/posture' },
      { title: 'RGPD', icon: Eye, href: '/compliance-pro/rgpd' },
      { title: 'PIA (AIPD)', icon: FileCheck, href: '/compliance-pro/pia' },
      { title: 'DSR & PIA', icon: Users, href: '/compliance-pro/dsr' },
      { title: 'Consentement', icon: FileCheck, href: '/compliance-pro/consent' },
      { title: 'Loi 25', icon: Globe, href: '/compliance-pro/loi25' },
      { title: 'CCPA', icon: Globe, href: '/compliance-pro/ccpa' },
      { title: 'SOX', icon: CreditCard, href: '/compliance-pro/sox' },
      { title: 'PCI-DSS', icon: Heart, href: '/compliance-pro/pci-dss' },
      { title: 'Registre', icon: FileText, href: '/compliance-pro/register' },
      { title: 'Ecarts', icon: AlertTriangle, href: '/compliance-pro/gaps' },
      { title: 'Actions', icon: ListOrdered, href: '/compliance-pro/actions' },
      { title: 'Transferts', icon: Globe, href: '/compliance-pro/transfers' },
      { title: 'ISO 27701', icon: Shield, href: '/compliance-pro/iso27701' },
      { title: 'NIST Privacy', icon: Shield, href: '/compliance-pro/nist-privacy' },
      { title: 'Certification', icon: FileCheck, href: '/compliance-pro/certification' },
      { title: 'Cadres', icon: Library, href: '/compliance-pro/frameworks' },
      { title: 'Rapports', icon: BarChart3, href: '/compliance-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/compliance-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/compliance-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/compliance-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/compliance-pro/outils' },
      { title: 'Simulateur Violation', icon: Siren, href: '/compliance-pro/data-breach-simulator' },
      { title: 'Transferts Transfrontaliers', icon: Globe, href: '/compliance-pro/cross-border-transfer' },
      { title: 'Dashboard Privacy', icon: Eye, href: '/compliance-pro/privacy-dashboard' },
      { title: 'Suivi Changements Régl.', icon: Newspaper, href: '/compliance-pro/regulatory-change-tracker' },
      { title: 'Monitoring Contrôles', icon: Activity, href: '/compliance-pro/control-monitoring' },
      { title: 'Contrôles de Conformité', icon: Shield, href: '/compliance-pro/controls' },
      { title: 'Surveillance continue (CCM)', icon: ScanLine, href: '/security-pro/ccm' },
      { title: "Analyse d'écart réglementaire", icon: Scale, href: '/compliance-pro/framework-gap' },
      { title: 'Questionnaire IA', icon: ClipboardCheck, href: '/compliance-pro/questionnaire-ia' },
    ],
  },
]

// Security questionnaires (RFP / vendor questionnaires) with AI auto-answer.
// Shown for CISO (C2), Compliance Officer (C5) and DPO (C3).
export const questionnairesNav: NavItem[] = [
  {
    title: 'Questionnaires', icon: ClipboardList,
    children: [
      { title: 'Questionnaires sécurité', icon: ClipboardList, href: '/compliance-pro/questionnaires' },
    ],
  },
]

export const changeProNav: NavItem[] = [
  {
    title: 'ChangePRO', icon: Repeat2,
    children: [
      { title: 'Hub Changement', icon: LayoutDashboard, href: '/change-pro' },
      { title: 'Cockpit Portfolio', icon: Target, href: '/change-pro/cockpit' },
      { title: 'Score Change', icon: Gauge, href: '/change-pro/score' },
      { title: 'Maturite', icon: TrendingUp, href: '/change-pro/maturite' },
      { title: 'ADKAR', icon: Layers, href: '/change-pro/adkar' },
      { title: 'Kotter 8 Steps', icon: TrendingUp, href: '/change-pro/kotter' },
      { title: 'Lewin & Bridges', icon: Waypoints, href: '/change-pro/lewin-bridges' },
      { title: 'McKinsey 7S', icon: Layers, href: '/change-pro/mckinsey-7s' },
      { title: 'Roadmap', icon: Map, href: '/change-pro/roadmap' },
      { title: 'Champions', icon: Crown, href: '/change-pro/champions' },
      { title: 'Adoption', icon: TrendingUp, href: '/change-pro/adoption' },
      { title: 'Resistance & SCARF', icon: AlertTriangle, href: '/change-pro/resistance' },
      { title: 'Impact', icon: Activity, href: '/change-pro/impact' },
      { title: 'Projets', icon: Boxes, href: '/change-pro/portfolio' },
      { title: 'Communication', icon: MessageSquare, href: '/change-pro/communication' },
      { title: 'Culture', icon: Globe, href: '/change-pro/culture' },
      { title: 'eNPS', icon: BarChart3, href: '/change-pro/enps' },
      { title: 'Pret au Change', icon: Gauge, href: '/change-pro/readiness' },
      { title: 'Formation', icon: BookOpen, href: '/change-pro/formation' },
      { title: 'ROI Change', icon: Calculator, href: '/change-pro/roi' },
      { title: 'Rapports', icon: BarChart3, href: '/change-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/change-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/change-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/change-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/change-pro/outils' },
    ],
  },
]

export const auditProNav: NavItem[] = [
  {
    title: 'AuditPRO', icon: SearchIcon,
    children: [
      { title: 'Hub Audit', icon: LayoutDashboard, href: '/audit-pro' },
      { title: 'Cockpit Audit', icon: BarChart3, href: '/audit-pro/cockpit' },
      { title: 'Calendrier', icon: Gauge, href: '/audit-pro/calendar' },
      { title: 'Plan Annuel', icon: Map, href: '/audit-pro/annual-plan' },
      { title: 'Assistant Plan', icon: Compass, href: '/audit-pro/plan-wizard' },
      { title: 'Univers Audit', icon: Globe, href: '/audit-pro/universe' },
      { title: 'Ressources', icon: Users, href: '/audit-pro/resources' },
      { title: 'Missions', icon: ClipboardCheck, href: '/audit-pro/missions' },
      { title: 'Papiers Travail', icon: FileText, href: '/audit-pro/workpapers' },
      { title: 'Checklists', icon: ListOrdered, href: '/audit-pro/checklists' },
      { title: 'Preuves', icon: FolderOpen, href: '/audit-pro/evidence' },
      { title: 'Self-Assessment', icon: Target, href: '/audit-pro/self-assessment' },
      { title: 'Constats & NC', icon: AlertTriangle, href: '/audit-pro/findings' },
      { title: 'Recommandations', icon: FileCheck, href: '/audit-pro/recommendations' },
      { title: 'Actions', icon: ListOrdered, href: '/audit-pro/actions' },
      { title: 'Suivi Actions', icon: Activity, href: '/audit-pro/tracking' },
      { title: 'Comite Audit', icon: Users, href: '/audit-pro/committee' },
      { title: 'Certification', icon: Shield, href: '/audit-pro/cert-dashboard' },
      { title: 'Pret Certif', icon: FileCheck, href: '/audit-pro/cert-readiness' },
      { title: 'Ecarts Certif', icon: AlertTriangle, href: '/audit-pro/cert-gaps' },
      { title: 'Timeline Certif', icon: Map, href: '/audit-pro/cert-timeline' },
      { title: 'Controles Certif', icon: Shield, href: '/audit-pro/cert-controls' },
      { title: 'Templates', icon: FileText, href: '/audit-pro/templates' },
      { title: 'Rapports', icon: BarChart3, href: '/audit-pro/reports' },
      { title: 'Journal', icon: Activity, href: '/audit-pro/audit-trail' },
      { title: 'Integration', icon: Plug, href: '/audit-pro/integration' },
      { title: 'Settings', icon: Settings, href: '/audit-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/audit-pro/outils' },
    ],
  },
]

export const aiIaProNav: NavItem[] = [
  {
    title: 'AI-IAGPRO', icon: BrainCircuit,
    children: [
      { title: 'Hub IA', icon: LayoutDashboard, href: '/ai-ia-pro' },
      { title: 'Cockpit NIST AI', icon: Shield, href: '/ai-ia-pro/cockpit' },
      { title: 'Score IA', icon: Gauge, href: '/ai-ia-pro/score' },
      { title: 'Pret IA', icon: Target, href: '/ai-ia-pro/readiness' },
      { title: 'Registre Modeles', icon: Boxes, href: '/ai-ia-pro/registre' },
      { title: 'Registre ML (MLOps)', icon: Boxes, href: '/ai-ia-pro/ml-registry' },
      { title: 'Lifecycle', icon: RefreshCw, href: '/ai-ia-pro/lifecycle' },
      { title: 'Gouvernance GenAI', icon: FileText, href: '/ai-ia-pro/genai-policies' },
      { title: 'Shadow AI', icon: Eye, href: '/ai-ia-pro/shadow-ai' },
      { title: 'Gouvernance Prompts', icon: FileText, href: '/ai-ia-pro/prompt-gov' },
      { title: 'Gouvernance Donnees', icon: Database, href: '/ai-ia-pro/data-gov' },
      { title: 'MLOps & Monitoring', icon: Activity, href: '/ai-ia-pro/mlops' },
      { title: 'Risques & Biais', icon: AlertTriangle, href: '/ai-ia-pro/risk-class' },
      { title: 'Biais IA', icon: Scale, href: '/ai-ia-pro/bias' },
      { title: 'Explicabilite', icon: Eye, href: '/ai-ia-pro/explain' },
      { title: 'Explicabilite (par prediction)', icon: ScanSearch, href: '/ai-ia-pro/explainability' },
      { title: 'Impact IA', icon: Activity, href: '/ai-ia-pro/impact' },
      { title: 'Performance IA', icon: ClipboardCheck, href: '/ai-ia-pro/performance' },
      { title: 'NIST AI RMF', icon: Shield, href: '/ai-ia-pro/nist-ai' },
      { title: 'EU AI Act', icon: Gavel, href: '/ai-ia-pro/eu-ai-act' },
      { title: 'ISO 42001', icon: FileCheck, href: '/ai-ia-pro/iso42001' },
      { title: 'Cadres IA', icon: Scale, href: '/ai-ia-pro/frameworks' },
      { title: 'Rapports', icon: BarChart3, href: '/ai-ia-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/ai-ia-pro/audit-trail' },
      { title: 'Integration', icon: Plug, href: '/ai-ia-pro/integration' },
      { title: 'Settings', icon: Settings, href: '/ai-ia-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/ai-ia-pro/outils' },
      { title: 'Dashboard Systèmes IA', icon: LayoutDashboard, href: '/ai-ia-pro/ai-system-dashboard' },
      { title: 'Moniteur Drift Modèle', icon: TrendingDown, href: '/ai-ia-pro/model-drift-monitor' },
      { title: 'Dashboard IA Responsable', icon: Shield, href: '/ai-ia-pro/responsible-ai-dash' },
      { title: 'Gouvernance IA Continue', icon: BrainCircuit, href: '/ai-ia-pro/ai-governance' },
    ],
  },
]

// ─── AIGOV360 — Jurisdiction packs + legal-status + multi-jurisdiction scoping ──
export const jurisdictionNav: NavItem[] = [
  {
    title: 'Juridictions & Statut légal', icon: Globe,
    children: [
      { title: 'Vue d\'ensemble', icon: LayoutDashboard, href: '/jurisdiction' },
      { title: 'Packs de juridiction', icon: Layers, href: '/jurisdiction/packs' },
      { title: 'Cadrage multi-juridiction', icon: Compass, href: '/jurisdiction/scoping' },
      { title: 'DDQ Souveraineté', icon: ShieldCheck, href: '/jurisdiction/sovereignty-ddq' },
    ],
  },
]

export const vendorProNav: NavItem[] = [
  {
    title: 'VendorPRO', icon: Building2,
    children: [
      { title: 'Hub Vendor', icon: LayoutDashboard, href: '/vendor-pro' },
      { title: 'Cockpit Registre', icon: FileText, href: '/vendor-pro/cockpit' },
      { title: 'Score Vendor', icon: Gauge, href: '/vendor-pro/score' },
      { title: 'Maturite Vendor', icon: TrendingUp, href: '/vendor-pro/maturity' },
      { title: 'TPRM & Risque', icon: Shield, href: '/vendor-pro/tprm' },
      { title: 'Due Diligence', icon: SearchIcon, href: '/vendor-pro/due-diligence' },
      { title: 'Concentration', icon: Radar, href: '/vendor-pro/concentration' },
      { title: 'Sous-Traitants', icon: Users, href: '/vendor-pro/subprocessors' },
      { title: 'Contrats & SLA', icon: FileCheck, href: '/vendor-pro/contracts' },
      { title: 'SLA Monitoring', icon: Activity, href: '/vendor-pro/sla' },
      { title: 'Renouvellements', icon: RefreshCw, href: '/vendor-pro/renewals' },
      { title: 'Couts', icon: CreditCard, href: '/vendor-pro/costs' },
      { title: 'Scorecards', icon: Target, href: '/vendor-pro/scorecards' },
      { title: 'Performance', icon: LineChart, href: '/vendor-pro/performance' },
      { title: 'Catalogue', icon: Boxes, href: '/vendor-pro/catalogue' },
      { title: 'Benchmarks', icon: BarChart3, href: '/vendor-pro/benchmarks' },
      { title: 'RGPD Vendor', icon: Eye, href: '/vendor-pro/rgpd' },
      { title: 'Clauses', icon: FileText, href: '/vendor-pro/clauses' },
      { title: 'Audit Vendor', icon: SearchIcon, href: '/vendor-pro/vendor-audit' },
      { title: 'Certifications', icon: Lock, href: '/vendor-pro/certifications' },
      { title: 'Rapports', icon: BarChart3, href: '/vendor-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/vendor-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/vendor-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/vendor-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/vendor-pro/outils' },
      { title: 'Surveillance fournisseurs continue', icon: Activity, href: '/vendor-pro/continuous-monitoring' },
      { title: 'Notations securite', icon: ShieldCheck, href: '/vendor-pro/ratings' },
      { title: 'Graphe n-ieme partie', icon: Network, href: '/vendor-pro/nth-party-graph' },
      { title: 'TPRM N-ième partie', icon: Waypoints, href: '/vendor-pro/nth-party' },
      { title: 'SBOM', icon: Boxes, href: '/vendor-pro/sbom' },
    ],
  },
]

export const dashboardProNav: NavItem[] = [
  {
    title: 'DashboardPRO', icon: PanelsTopLeft,
    children: [
      { title: 'Hub Executif', icon: LayoutDashboard, href: '/dashboard-pro' },
      { title: 'Score Global', icon: Gauge, href: '/dashboard-pro/score' },
      { title: 'Alertes', icon: AlertTriangle, href: '/dashboard-pro/alertes' },
      { title: 'C-Suite', icon: Crown, href: '/dashboard-pro/csuite' },
      { title: 'Panorama 12M', icon: PanelsTopLeft, href: '/dashboard-pro/panorama' },
      { title: 'Drill-Down', icon: Crosshair, href: '/dashboard-pro/drilldown' },
      { title: 'Correlations', icon: Waypoints, href: '/dashboard-pro/correlations' },
      { title: 'Heatmap', icon: Layers, href: '/dashboard-pro/heatmap' },
      { title: 'Score Consolide', icon: Target, href: '/dashboard-pro/score-consolide' },
      { title: 'Maturite', icon: TrendingUp, href: '/dashboard-pro/maturite' },
      { title: 'Risque', icon: AlertTriangle, href: '/dashboard-pro/risque' },
      { title: 'Conformite', icon: Shield, href: '/dashboard-pro/conformite' },
      { title: 'Rapport CA', icon: BarChart3, href: '/dashboard-pro/rapport-ca' },
      { title: 'Tableau CA', icon: BarChart3, href: '/dashboard-pro/tableau-ca' },
      { title: 'Tendances', icon: LineChart, href: '/dashboard-pro/tendances' },
      { title: 'KPIs', icon: BarChart3, href: '/dashboard-pro/kpis' },
      { title: 'Multi-Site', icon: Globe, href: '/dashboard-pro/multi-site' },
      { title: 'Comparaison', icon: Layers, href: '/dashboard-pro/comparaison' },
      { title: 'Benchmarks', icon: Target, href: '/dashboard-pro/benchmarks' },
      { title: 'Deploiement', icon: Activity, href: '/dashboard-pro/deploiement' },
      { title: 'Configuration', icon: Settings, href: '/dashboard-pro/config' },
      { title: 'Audit Trail', icon: Activity, href: '/dashboard-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/dashboard-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/dashboard-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/dashboard-pro/outils' },
      { title: 'Intelligence Concurrentielle', icon: Radar, href: '/dashboard-pro/competitive-intel' },
      { title: 'Anomalies KPI', icon: TrendingDown, href: '/dashboard-pro/kpi-anomaly' },
    ],
  },
]

export const ethicsProNav: NavItem[] = [
  {
    title: 'EthicsPRO', icon: Scale,
    children: [
      { title: 'Hub Ethique', icon: LayoutDashboard, href: '/ethics-pro' },
      { title: 'Cockpit Ethique', icon: BarChart3, href: '/ethics-pro/cockpit' },
      { title: 'Score Ethique', icon: Gauge, href: '/ethics-pro/score' },
      { title: 'Maturite Ethique', icon: TrendingUp, href: '/ethics-pro/maturite' },
      { title: 'Politiques', icon: FileText, href: '/ethics-pro/politiques' },
      { title: 'Code Conduite', icon: BookOpen, href: '/ethics-pro/code-conduite' },
      { title: 'Formation', icon: BookOpen, href: '/ethics-pro/formation' },
      { title: 'Comite Ethique', icon: Users, href: '/ethics-pro/comite-ethique' },
      { title: 'Signalements', icon: Siren, href: '/ethics-pro/signalements' },
      { title: 'Alerte Ethique', icon: AlertTriangle, href: '/ethics-pro/alerte' },
      { title: 'Conflit Interet', icon: Scale, href: '/ethics-pro/conflit-interet' },
      { title: 'Anti-Corruption', icon: Shield, href: '/ethics-pro/anti-corruption' },
      { title: 'Enquetes', icon: SearchIcon, href: '/ethics-pro/enquetes' },
      { title: 'Culture & ESG', icon: Globe, href: '/ethics-pro/culture' },
      { title: 'Diversite', icon: Heart, href: '/ethics-pro/diversite' },
      { title: 'ESG', icon: Globe, href: '/ethics-pro/esg' },
      { title: 'ISO 37001', icon: Shield, href: '/ethics-pro/iso37001' },
      { title: 'Sapin II', icon: Gavel, href: '/ethics-pro/sapin-ii' },
      { title: 'Certification', icon: FileCheck, href: '/ethics-pro/certification' },
      { title: 'Cadres', icon: Library, href: '/ethics-pro/frameworks' },
      { title: 'Rapports', icon: BarChart3, href: '/ethics-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/ethics-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/ethics-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/ethics-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/ethics-pro/outils' },
    ],
  },
]

export const maturityProNav: NavItem[] = [
  {
    title: 'MaturityPRO', icon: TrendingUp,
    children: [
      { title: 'Hub Maturite', icon: LayoutDashboard, href: '/maturity-pro' },
      { title: 'Cockpit Scoring', icon: Gauge, href: '/maturity-pro/cockpit' },
      { title: 'Score Maturite', icon: Gauge, href: '/maturity-pro/score' },
      { title: 'Maturite', icon: TrendingUp, href: '/maturity-pro/maturite' },
      { title: 'CMMI', icon: Layers, href: '/maturity-pro/cmmi' },
      { title: 'COBIT 2019', icon: Shield, href: '/maturity-pro/cobit' },
      { title: 'ISO 33001', icon: FileCheck, href: '/maturity-pro/iso33001' },
      { title: 'Modele Custom', icon: Wrench, href: '/maturity-pro/custom-model' },
      { title: 'Evaluations', icon: ClipboardCheck, href: '/maturity-pro/campaigns' },
      { title: 'Questionnaires', icon: FileText, href: '/maturity-pro/questionnaires' },
      { title: 'Dimensions', icon: Compass, href: '/maturity-pro/dimensions' },
      { title: 'Benchmarking', icon: BarChart3, href: '/maturity-pro/benchmarking' },
      { title: 'Gap Analyse', icon: Target, href: '/maturity-pro/gap-analyse' },
      { title: 'Progression', icon: TrendingUp, href: '/maturity-pro/progression' },
      { title: 'Objectifs', icon: Target, href: '/maturity-pro/objectifs' },
      { title: 'Roadmap', icon: Milestone, href: '/maturity-pro/roadmap' },
      { title: 'Mapping', icon: Map, href: '/maturity-pro/mapping' },
      { title: 'Certification', icon: FileCheck, href: '/maturity-pro/certification' },
      { title: 'Benchmarks', icon: BarChart3, href: '/maturity-pro/benchmarks' },
      { title: 'Cadres', icon: Library, href: '/maturity-pro/frameworks' },
      { title: 'Rapports', icon: BarChart3, href: '/maturity-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/maturity-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/maturity-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/maturity-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/maturity-pro/outils' },
    ],
  },
]

export const strategyProNav: NavItem[] = [
  {
    title: 'StrategyPRO', icon: Target,
    children: [
      { title: 'Hub Strategie', icon: LayoutDashboard, href: '/strategy-pro' },
      { title: 'Cockpit OKR', icon: Target, href: '/strategy-pro/cockpit' },
      { title: 'Score Strategie', icon: Gauge, href: '/strategy-pro/score' },
      { title: 'Vision', icon: Eye, href: '/strategy-pro/vision' },
      { title: 'OKR Dashboard', icon: TrendingUp, href: '/strategy-pro/okr' },
      { title: 'Objectifs', icon: Milestone, href: '/strategy-pro/objectifs' },
      { title: 'Key Results', icon: LineChart, href: '/strategy-pro/key-results' },
      { title: 'Alignement', icon: Target, href: '/strategy-pro/alignement' },
      { title: 'BSC Perspectives', icon: Layers, href: '/strategy-pro/bsc' },
      { title: 'Perspectives', icon: Eye, href: '/strategy-pro/perspectives' },
      { title: 'Carte Strategique', icon: Map, href: '/strategy-pro/strategic-map' },
      { title: 'Initiatives', icon: Boxes, href: '/strategy-pro/initiatives' },
      { title: 'SWOT', icon: Activity, href: '/strategy-pro/swot' },
      { title: 'PESTEL', icon: Globe, href: '/strategy-pro/pestel' },
      { title: 'Business Case', icon: Calculator, href: '/strategy-pro/business-case' },
      { title: 'ROI', icon: Calculator, href: '/strategy-pro/roi' },
      { title: 'Portfolio', icon: Boxes, href: '/strategy-pro/portfolio' },
      { title: 'Programmes', icon: Layers, href: '/strategy-pro/programs' },
      { title: 'Gouvernance', icon: Crown, href: '/strategy-pro/governance' },
      { title: 'Cadres', icon: Library, href: '/strategy-pro/frameworks' },
      { title: 'Rapports', icon: BarChart3, href: '/strategy-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/strategy-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/strategy-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/strategy-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/strategy-pro/outils' },
    ],
  },
]

export const workspaceProNav: NavItem[] = [
  {
    title: 'WorkspacePRO', icon: FolderKanban,
    children: [
      { title: 'Hub Workspace', icon: LayoutDashboard, href: '/workspace-pro' },
      { title: 'Workspaces', icon: FolderKanban, href: '/workspace-pro/workspaces' },
      { title: 'Activite', icon: Activity, href: '/workspace-pro/activity' },
      { title: 'Demarrage Rapide', icon: Target, href: '/workspace-pro/quick-start' },
      { title: 'Chat IA', icon: MessageSquare, href: '/workspace-pro/chat' },
      { title: 'Historique Chat', icon: MessageSquare, href: '/workspace-pro/chat-history' },
      { title: 'Documents', icon: FileText, href: '/workspace-pro/documents' },
      { title: 'Outils', icon: Wrench, href: '/workspace-pro/tools' },
      { title: 'Recherche', icon: SearchIcon, href: '/workspace-pro/search' },
      { title: 'Communaute', icon: Users, href: '/workspace-pro/community' },
      { title: 'Themes', icon: Layers, href: '/workspace-pro/themes' },
      { title: 'Membres', icon: Users, href: '/workspace-pro/members' },
      { title: 'Invitations', icon: Users, href: '/workspace-pro/invitations' },
      { title: 'RAG Index', icon: Database, href: '/workspace-pro/rag-index' },
      { title: 'Agents IA', icon: BrainCircuit, href: '/workspace-pro/agents' },
      { title: 'Permissions', icon: Lock, href: '/workspace-pro/permissions' },
      { title: 'Acces Web', icon: Globe, href: '/workspace-pro/web-access' },
      { title: 'Notifications', icon: Activity, href: '/workspace-pro/notifications' },
      { title: 'Rapports', icon: BarChart3, href: '/workspace-pro/reports' },
      { title: 'Audit Trail', icon: Activity, href: '/workspace-pro/audit-trail' },
      { title: 'Integration', icon: Plug, href: '/workspace-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/workspace-pro/settings' },
      { title: 'Module Settings', icon: Settings, href: '/workspace-pro/module-settings' },
      { title: 'Page Outils', icon: Wrench, href: '/workspace-pro/outils' },
      { title: 'Mon Radar Risques', icon: Radar, href: '/workspace-pro/personal-risk-radar' },
      { title: 'Hub de Connaissances', icon: BookOpen, href: '/workspace-pro/knowledge-hub' },
      { title: 'Notes → Actions', icon: ClipboardList, href: '/workspace-pro/meeting-actions' },
    ],
  },
]

export const businessModelProNav: NavItem[] = [
  {
    title: 'BusinessModelPRO', icon: Grid2x2,
    children: [
      { title: 'Hub Business', icon: LayoutDashboard, href: '/business-model-pro' },
      { title: 'Cockpit', icon: Gauge, href: '/business-model-pro/cockpit' },
      { title: 'Score', icon: Gauge, href: '/business-model-pro/score' },
      { title: 'Maturite', icon: TrendingUp, href: '/business-model-pro/maturite' },
      { title: 'Projet IA', icon: BrainCircuit, href: '/business-model-pro/ai-project' },
      { title: 'Modele Affaires', icon: Grid2x2, href: '/business-model-pro/business-model' },
      { title: 'Canevas Custom', icon: Layers, href: '/business-model-pro/custom-canvas' },
      { title: 'Bibliotheque', icon: Library, href: '/business-model-pro/canvas-library' },
      { title: 'Sessions', icon: Users, href: '/business-model-pro/sessions' },
      { title: 'Contributions', icon: Users, href: '/business-model-pro/contributions' },
      { title: 'Consolidation', icon: Layers, href: '/business-model-pro/consolidation' },
      { title: 'Pleniere', icon: Users, href: '/business-model-pro/plenary' },
      { title: 'Alignement', icon: Target, href: '/business-model-pro/strategic-alignment' },
      { title: 'Analyse Impact', icon: Activity, href: '/business-model-pro/impact-analysis' },
      { title: 'Questions Guidees', icon: MessageSquare, href: '/business-model-pro/guided-questions' },
      { title: 'Lean Canvas', icon: Grid2x2, href: '/business-model-pro/lean-canvas' },
      { title: 'Value Proposition', icon: Target, href: '/business-model-pro/value-proposition' },
      { title: 'Benchmarking', icon: BarChart3, href: '/business-model-pro/benchmarking' },
      { title: 'Certification', icon: Shield, href: '/business-model-pro/certification' },
      { title: 'Frameworks', icon: BookOpen, href: '/business-model-pro/frameworks' },
      { title: 'Rapports', icon: BarChart3, href: '/business-model-pro/reports' },
      { title: 'Audit Trail', icon: Activity, href: '/business-model-pro/audit-trail' },
      { title: 'Integrations', icon: Plug, href: '/business-model-pro/integrations' },
      { title: 'Settings', icon: Settings, href: '/business-model-pro/settings' },
      { title: 'Outils', icon: Wrench, href: '/business-model-pro/outils' },
    ],
  },
]

// ─── Modules récemment ajoutés (routes existent, nav manquait) ──────────────

export const caseMgmtNav: NavItem[] = [
  {
    title: 'Gestion des Cas', icon: FolderOpen,
    children: [
      { title: 'Hub des Cas', icon: LayoutDashboard, href: '/case-mgmt/hub' },
      { title: 'Cas', icon: FolderOpen, href: '/case-mgmt/cases' },
      { title: 'Analytique', icon: BarChart3, href: '/case-mgmt/analytics' },
      { title: 'Audit Trail', icon: Activity, href: '/case-mgmt/audit-trail' },
      { title: 'Paramètres', icon: Settings, href: '/case-mgmt/settings' },
    ],
  },
]

export const pdcaNav: NavItem[] = [
  {
    title: 'PDCA', icon: RefreshCw,
    children: [
      { title: 'Hub PDCA', icon: LayoutDashboard, href: '/pdca/hub' },
      { title: 'Cycles', icon: RefreshCw, href: '/pdca/cycles' },
      { title: 'Amélioration', icon: TrendingUp, href: '/pdca/improvement' },
      { title: 'Revue de Direction', icon: ClipboardCheck, href: '/pdca/management-review' },
    ],
  },
]

export const regChangeNav: NavItem[] = [
  {
    title: 'Changement Réglementaire', icon: Newspaper,
    children: [
      { title: 'Hub', icon: LayoutDashboard, href: '/reg-change/hub' },
      { title: 'Alertes', icon: Bell, href: '/reg-change/alerts' },
      { title: 'Impacts', icon: AlertTriangle, href: '/reg-change/impacts' },
      { title: 'Analyse Impact', icon: Activity, href: '/reg-change/impact-analysis' },
      { title: 'Actions', icon: ListOrdered, href: '/reg-change/actions' },
      { title: 'Obligations', icon: FileCheck, href: '/reg-change/obligations' },
      { title: 'Flux Réglementaire', icon: Newspaper, href: '/reg-change/feed' },
      { title: 'Horizon Réglementaire', icon: Radar, href: '/reg-change/horizon' },
      { title: 'Audit Trail', icon: Activity, href: '/reg-change/audit-trail' },
      { title: 'Paramètres', icon: Settings, href: '/reg-change/settings' },
    ],
  },
]

// M13 Continuous Watch (veille) — AIGOV360 AI-watch nav section.
export const watchNav: NavItem[] = [
  {
    title: 'Veille IA (Continuous Watch)', icon: Radar,
    children: [
      { title: 'Indicateurs (KPIs)', icon: Gauge, href: '/reg-change/watch-kpis' },
      { title: "Analyses d'Impact & Décisions", icon: Activity, href: '/reg-change/impact-analysis' },
      { title: 'Expositions Technologiques', icon: Cpu, href: '/reg-change/tech-exposures' },
      { title: 'Veille Juridique', icon: Scale, href: '/reg-change/legal-proposals' },
      { title: 'Digests & Routage', icon: Newspaper, href: '/reg-change/digests' },
      { title: 'Changements de Pack', icon: GitBranch, href: '/reg-change/pack-changes' },
    ],
  },
]

export const regEngagementNav: NavItem[] = [
  {
    title: 'Engagement Régulateur', icon: Gavel,
    children: [
      { title: 'Hub', icon: LayoutDashboard, href: '/reg-engagement/hub' },
      { title: 'Engagements', icon: FileText, href: '/reg-engagement/engagements' },
      { title: 'Échéances', icon: Calendar, href: '/reg-engagement/deadlines' },
      { title: 'Soumissions', icon: FileCheck, href: '/reg-engagement/submissions' },
    ],
  },
]

export const policyMgmtNav: NavItem[] = [
  {
    title: 'Gestion Politiques', icon: FileText,
    children: [
      { title: 'Hub', icon: LayoutDashboard, href: '/policy-mgmt/hub' },
      { title: 'Politiques', icon: FileText, href: '/policy-mgmt/policies' },
      { title: 'Attestations', icon: Award, href: '/policy-mgmt/attestations' },
      { title: 'Exceptions', icon: AlertTriangle, href: '/policy-mgmt/exceptions' },
      { title: 'Recherche', icon: SearchIcon, href: '/policy-mgmt/search' },
      { title: 'Audit Trail', icon: Activity, href: '/policy-mgmt/audit-trail' },
      { title: 'Paramètres', icon: Settings, href: '/policy-mgmt/settings' },
    ],
  },
]

export const aiIntelligenceNav: NavItem[] = [
  {
    title: 'AI Intelligence', icon: Brain,
    children: [
      { title: 'Dashboard', icon: LayoutDashboard, href: '/ai-intelligence/dashboard' },
      { title: 'Classifications', icon: Layers, href: '/ai-intelligence/classifications' },
      { title: 'Prédictions', icon: TrendingUp, href: '/ai-intelligence/predictions' },
      { title: 'Analyse Documents', icon: FileText, href: '/ai-intelligence/document-analysis' },
      { title: 'Document Intelligence', icon: FileSearch, href: '/ai-intelligence/doc-intelligence', featureId: 'doc_intel' },
      { title: 'Paramètres', icon: Settings, href: '/ai-intelligence/settings' },
    ],
  },
]

export const orgContextNav: NavItem[] = [
  {
    title: 'Contexte Organisation', icon: Building2,
    children: [
      { title: 'Hub', icon: LayoutDashboard, href: '/org-context/hub' },
      { title: 'Objectifs Stratégiques', icon: Target, href: '/org-context/strategic-goals' },
      { title: 'Alignement', icon: Waypoints, href: '/org-context/alignment' },
      { title: 'Contraintes', icon: AlertTriangle, href: '/org-context/constraints' },
    ],
  },
]

export const transformationProNav: NavItem[] = [
  {
    title: 'TransformationPRO', icon: GitBranch,
    children: [
      { title: 'Hub', icon: LayoutDashboard, href: '/transformation-pro/hub' },
      { title: 'Programmes', icon: Boxes, href: '/transformation-pro/programs' },
      { title: 'Dette Technique', icon: AlertTriangle, href: '/transformation-pro/tech-debt' },
      { title: 'Adoption', icon: TrendingUp, href: '/transformation-pro/adoption' },
      { title: 'Roadmap', icon: Map, href: '/transformation-pro/roadmap' },
      { title: 'Étapes', icon: Milestone, href: '/transformation-pro/steps' },
    ],
  },
]

// ─── Risk Intelligence (FAIR, RCSA, GRC Agents, ROI, Predictive) ────────────
export const riskIntelligenceNav: NavItem[] = [
  {
    title: 'Risk Intelligence', icon: Radar,
    children: [
      { title: 'Quantification FAIR', icon: Calculator, href: '/risk-intelligence/fair' },
      { title: 'Campagnes RCSA', icon: ClipboardCheck, href: '/risk-intelligence/rcsa' },
      { title: 'Agents GRC (IA)', icon: Bot, href: '/risk-intelligence/agents' },
      { title: 'Marketplace', icon: Boxes, href: '/risk-intelligence/marketplace' },
      { title: 'Calculateur ROI', icon: Calculator, href: '/risk-intelligence/roi' },
      { title: 'Automatisation Preuves', icon: Zap, href: '/risk-intelligence/evidence-automation' },
      { title: 'Analytique Appétit Risque', icon: Gauge, href: '/risk-intelligence/risk-appetite' },
      { title: 'Risque Prédictif', icon: TrendingUp, href: '/risk-intelligence/predictive' },
    ],
  },
]

// ─── Évaluation & 360° (evaluate providers/auditors/consultants/services) ────
export const evaluationNav: NavItem[] = [
  {
    title: 'Évaluation & 360°', icon: Star,
    children: [
      { title: 'Vue d\'ensemble', icon: LayoutDashboard, href: '/evaluation' },
      { title: 'Critères & Modèles', icon: SlidersHorizontal, href: '/evaluation/criteria' },
      { title: 'Cycles', icon: RefreshCw, href: '/evaluation/cycles' },
      { title: 'Évaluations', icon: ClipboardCheck, href: '/evaluation/evaluations' },
      { title: 'Scorecards & Classement', icon: Trophy, href: '/evaluation/scorecards' },
      { title: 'Analytique', icon: BarChart3, href: '/evaluation/analytics' },
      { title: 'Plans d\'action', icon: ListChecks, href: '/evaluation/actions' },
    ],
  },
]

// ─── Connected GRC (crosswalks, contrôles communs, KRI/KCI/KPI, personas) ────
export const connectedGrcNav: NavItem[] = [
  {
    title: 'Connected GRC', icon: Link2,
    children: [
      { title: 'Dashboard Persona', icon: LayoutDashboard, href: '/connected-grc/dashboard' },
      { title: 'Crosswalks (Référentiels)', icon: GitBranch, href: '/connected-grc/crosswalks' },
      { title: 'Contrôles Communs', icon: Shield, href: '/connected-grc/common-controls' },
      { title: 'Moniteur Métriques (KRI/KCI/KPI)', icon: Activity, href: '/connected-grc/metrics' },
    ],
  },
]

export const esgNav: NavItem[] = [
  {
    title: 'ESG', icon: Leaf,
    children: [
      { title: 'Hub ESG', icon: LayoutDashboard, href: '/esg/hub' },
      { title: 'CSRD', icon: FileText, href: '/esg/csrd' },
      { title: 'Carbone', icon: Leaf, href: '/esg/carbon' },
      { title: 'KPIs', icon: BarChart3, href: '/esg/kpis' },
      { title: 'Double Matérialité', icon: Layers, href: '/esg/double-materiality' },
      { title: 'Reporting ESG', icon: FileBarChart, href: '/esg/reporting' },
      { title: 'Émissions GES', icon: Cloud, href: '/esg/ghg-emissions' },
      { title: 'Supply Chain', icon: Network, href: '/esg/supply-chain' },
    ],
  },
]

export const iso42001Nav: NavItem[] = [
  {
    title: 'ISO 42001', icon: FileCheck,
    children: [
      { title: 'Dashboard', icon: LayoutDashboard, href: '/iso42001/dashboard' },
      { title: 'Templates', icon: FileText, href: '/iso42001/templates' },
      { title: 'Créer Évaluation', icon: ClipboardCheck, href: '/iso42001/create' },
      { title: 'Consolidation', icon: Layers, href: '/iso42001/consolidation' },
      { title: 'Analyse Dimension', icon: Compass, href: '/iso42001/dimension' },
      { title: 'Paramètres', icon: Settings, href: '/iso42001/settings' },
    ],
  },
]

export const taxProNav: NavItem[] = [
  {
    title: 'TaxPRO', icon: Calculator,
    children: [
      { title: 'Hub Fiscal', icon: LayoutDashboard, href: '/tax-pro' },
      { title: 'Cockpit', icon: Gauge, href: '/tax-pro/cockpit' },
      { title: 'Score', icon: Gauge, href: '/tax-pro/score' },
      { title: 'Info Contribuable', icon: Users, href: '/tax-pro/taxpayer-info' },
      { title: 'Conjoint', icon: Users, href: '/tax-pro/spouse-info' },
      { title: 'Personnes a charge', icon: Users, href: '/tax-pro/dependents' },
      { title: 'Assurance medicaments', icon: Shield, href: '/tax-pro/drug-insurance' },
      { title: 'Questions fiscales', icon: FileText, href: '/tax-pro/fiscal-questions' },
      { title: 'Preferences', icon: Settings, href: '/tax-pro/preferences' },
      { title: 'Checklist', icon: FileCheck, href: '/tax-pro/checklist' },
      { title: 'Feuillets', icon: FileText, href: '/tax-pro/slips' },
      { title: 'Travail autonome', icon: Boxes, href: '/tax-pro/self-employment' },
      { title: 'Revenus TA', icon: CreditCard, href: '/tax-pro/se-income' },
      { title: 'Depenses TA', icon: CreditCard, href: '/tax-pro/se-expenses' },
      { title: 'Vehicule', icon: Compass, href: '/tax-pro/vehicle-expenses' },
      { title: 'Bureau domicile', icon: Building2, href: '/tax-pro/home-office' },
      { title: 'TPS/TVQ', icon: Calculator, href: '/tax-pro/gst-qst' },
      { title: 'Immeubles locatifs', icon: Building2, href: '/tax-pro/rental-properties' },
      { title: 'Revenus locatifs', icon: CreditCard, href: '/tax-pro/rental-income' },
      { title: 'Depenses locatives', icon: CreditCard, href: '/tax-pro/rental-expenses' },
      { title: 'Deductions', icon: Scale, href: '/tax-pro/deductions' },
      { title: 'Frais medicaux', icon: Heart, href: '/tax-pro/medical-expenses' },
      { title: 'Dons', icon: Heart, href: '/tax-pro/donations' },
      { title: 'Estimation', icon: Calculator, href: '/tax-pro/estimate' },
      { title: 'Optimisation', icon: TrendingUp, href: '/tax-pro/optimization' },
      { title: 'Soumission', icon: FileCheck, href: '/tax-pro/submission' },
      { title: 'Rapport executif', icon: BarChart3, href: '/tax-pro/report-exec' },
      { title: 'Audit Trail', icon: Activity, href: '/tax-pro/audit-trail' },
      { title: 'Settings', icon: Settings, href: '/tax-pro/settings' },
    ],
  },
]

// ─── Automation (RPA Flows) — Phase 3b ─────────────────────────────────────

export const automationNav: NavItem[] = [
  {
    title: 'Automatisation', icon: Zap,
    children: [
      { title: "Flux d'Automatisation", icon: Zap, href: '/automation/flows' },
      { title: 'Générateur de Workflows', icon: GitBranch, href: '/workflow-builder' },
    ],
  },
]

// ─── AI Copilot nav sections (per persona) ─────────────────────────────────

export const dirigeantCopilotNav: NavItem[] = [
  {
    title: 'AI Copilot — Dirigeant', icon: Sparkles,
    children: [
      { title: 'Briefing Exécutif', icon: Zap, href: '/dashboard-pro/executive-briefing', featureId: 'copilot.executive_briefing' },
      { title: 'Préparation CA', icon: ClipboardCheck, href: '/dashboard-pro/board-readiness', featureId: 'copilot.board_readiness' },
      { title: 'Radar Risques Stratégiques', icon: Target, href: '/dashboard-pro/strategic-risk-radar', featureId: 'copilot.strategic_radar' },
      { title: 'Aide à la Décision', icon: Brain, href: '/dashboard-pro/decision-support', featureId: 'copilot.decision_support' },
      { title: 'Carte Parties Prenantes', icon: Users, href: '/dashboard-pro/stakeholder-map', featureId: 'copilot.stakeholder_map' },
      { title: 'Tableau ESG', icon: Leaf, href: '/dashboard-pro/esg-scoreboard', featureId: 'copilot.esg_scoreboard' },
      { title: 'Board Pack Auto', icon: FileBarChart, href: '/dashboard-pro/board-pack-generator', featureId: 'copilot.board_pack' },
      { title: 'Intelligence Concurrentielle', icon: Radar, href: '/dashboard-pro/competitive-intel', featureId: 'copilot.competitive_intel' },
      { title: 'Anomalies KPI', icon: TrendingDown, href: '/dashboard-pro/kpi-anomaly', featureId: 'copilot.kpi_anomaly' },
    ],
  },
]

export const cisoCopilotNav: NavItem[] = [
  {
    title: 'AI Copilot — CISO', icon: Sparkles,
    children: [
      { title: 'Posture Sécurité IA', icon: Shield, href: '/security-pro/security-posture-ai', featureId: 'copilot.security_posture' },
      { title: 'Playbooks Incidents', icon: BookOpen, href: '/security-pro/incident-playbook', featureId: 'copilot.incident_playbook' },
      { title: 'Surface d\'Attaque', icon: Crosshair, href: '/security-pro/attack-surface', featureId: 'copilot.attack_surface' },
      { title: 'Scanner Zero Trust', icon: Lock, href: '/security-pro/zero-trust-scorecard', featureId: 'copilot.zero_trust' },
      { title: 'Threat Hunting', icon: ScanSearch, href: '/security-pro/threat-hunting', featureId: 'copilot.threat_hunting' },
      { title: 'Sensibilisation Gamifiée', icon: Gamepad2, href: '/security-pro/awareness', featureId: 'copilot.security_game' },
      { title: 'Copilot Posture Sécurité', icon: ShieldCheck, href: '/security-pro/security-posture-copilot', featureId: 'copilot.security_posture_copilot' },
      { title: 'Scorecard Zero Trust', icon: CheckCircle, href: '/security-pro/zero-trust-scorecard', featureId: 'copilot.zero_trust_scorecard' },
    ],
  },
]

export const dpoCopilotNav: NavItem[] = [
  {
    title: 'AI Copilot — DPO', icon: Sparkles,
    children: [
      { title: 'Analytique Consentements', icon: PieChart, href: '/compliance-pro/consent-analytics', featureId: 'copilot.consent_analytics' },
      { title: 'Automatisation DSAR', icon: Zap, href: '/compliance-pro/dsar-automation', featureId: 'copilot.dsar_automation' },
      { title: 'Privacy by Design', icon: Shield, href: '/compliance-pro/privacy-by-design', featureId: 'copilot.pbd' },
      { title: 'Scanner Cookies', icon: Cookie, href: '/compliance-pro/cookie-scanner', featureId: 'copilot.cookie_scanner' },
      { title: 'Cartographie Flux Données', icon: GitBranch, href: '/compliance-pro/data-flow-mapper', featureId: 'copilot.data_flow_mapper' },
      { title: 'Wizard Notification Violation', icon: AlertTriangle, href: '/compliance-pro/breach-notification-wizard', featureId: 'copilot.breach_wizard' },
      { title: 'Générateur RoPA', icon: FileText, href: '/compliance-pro/ropa-generator', featureId: 'copilot.ropa_generator' },
      { title: 'Heatmap Risques Privacy', icon: Map, href: '/compliance-pro/privacy-risk-heatmap', featureId: 'copilot.privacy_heatmap' },
      { title: 'Analyseur Transferts', icon: ArrowLeftRight, href: '/compliance-pro/cross-border-analyzer', featureId: 'copilot.cross_border' },
      { title: 'Simulateur Violation Données', icon: Siren, href: '/compliance-pro/data-breach-simulator', featureId: 'copilot.data_breach_sim' },
      { title: 'Transferts Transfrontaliers', icon: Globe, href: '/compliance-pro/cross-border-transfer', featureId: 'copilot.cross_border_transfer' },
      { title: 'Tableau de Bord Privacy', icon: Eye, href: '/compliance-pro/privacy-dashboard', featureId: 'copilot.privacy_dashboard' },
    ],
  },
]

export const riskManagerCopilotNav: NavItem[] = [
  {
    title: 'AI Copilot — Risk Manager', icon: Sparkles,
    children: [
      { title: 'Simulateur Scénarios', icon: FlaskConical, href: '/risk-pro/scenario-simulator', featureId: 'copilot.scenario_simulator' },
      { title: 'Alertes Précoces', icon: Bell, href: '/risk-pro/early-warning', featureId: 'copilot.early_warning' },
      { title: 'Efficacité Contrôles', icon: CheckCircle, href: '/risk-pro/control-effectiveness', featureId: 'copilot.control_effectiveness' },
      { title: 'Appétit Risque', icon: Gauge, href: '/risk-pro/risk-appetite-dashboard', featureId: 'copilot.risk_appetite' },
      { title: 'Base Événements Perte', icon: Database, href: '/risk-pro/loss-events', featureId: 'copilot.loss_events' },
      { title: 'Capital OpRisk', icon: Calculator, href: '/risk-pro/capital-adequacy', featureId: 'copilot.oprisk_capital' },
      { title: 'Stress Testing', icon: Zap, href: '/risk-pro/montecarlo', featureId: 'copilot.stress_testing' },
      { title: 'Auto-Évaluation Contrôles', icon: ClipboardCheck, href: '/risk-pro/controls', featureId: 'copilot.csa' },
      { title: 'Agrégation Risques', icon: Layers, href: '/risk-pro/quantitative', featureId: 'copilot.risk_aggregation' },
      { title: 'Calibration KRI', icon: SlidersHorizontal, href: '/risk-pro/kri', featureId: 'copilot.kri_calibrator' },
      { title: 'Moniteur Appétit Risque', icon: Gauge, href: '/risk-pro/risk-appetite-monitor', featureId: 'copilot.risk_appetite_monitor' },
      { title: 'Analyse Événements Perte', icon: TrendingDown, href: '/risk-pro/loss-event-analysis', featureId: 'copilot.loss_event_analysis' },
    ],
  },
]

export const complianceOfficerCopilotNav: NavItem[] = [
  {
    title: 'AI Copilot — Compliance', icon: Sparkles,
    children: [
      { title: 'Impact Changements Réglementaires', icon: Newspaper, href: '/compliance-pro/regulatory-change-impact', featureId: 'copilot.reg_change' },
      { title: 'Harmonisation Multi-Cadres', icon: Layers, href: '/compliance-pro/multi-framework-harmonizer', featureId: 'copilot.multi_framework' },
      { title: 'Calendrier Conformité', icon: Calendar, href: '/compliance-pro/compliance-calendar', featureId: 'copilot.compliance_calendar' },
      { title: 'Cycle Vie Politiques', icon: RefreshCw, href: '/compliance-pro/policy-lifecycle', featureId: 'copilot.policy_lifecycle' },
      { title: 'Intelligence Réglementaire', icon: Radio, href: '/compliance-pro/regulatory-change-tracker', featureId: 'copilot.reg_intelligence' },
      { title: 'Suivi Changements Réglementaires', icon: Newspaper, href: '/compliance-pro/regulatory-change-tracker', featureId: 'copilot.reg_change_tracker' },
      { title: 'Monitoring Contrôles', icon: Activity, href: '/compliance-pro/control-monitoring', featureId: 'copilot.control_monitoring' },
    ],
  },
]

export const dataIaOfficerCopilotNav: NavItem[] = [
  {
    title: 'AI Copilot — Data & IA', icon: Sparkles,
    children: [
      { title: 'Monitoring Systèmes IA', icon: Activity, href: '/ai-ia-pro/ai-system-monitor', featureId: 'copilot.ai_monitor' },
      { title: 'Alertes Drift Modèle', icon: TrendingDown, href: '/ai-ia-pro/model-drift-alert', featureId: 'copilot.model_drift' },
      { title: 'Audit Équité IA', icon: Scale, href: '/ai-ia-pro/fairness-auditor', featureId: 'copilot.fairness_auditor' },
      { title: 'Registre Inventaire IA', icon: ListOrdered, href: '/ai-ia-pro/ai-inventory-register', featureId: 'copilot.ai_inventory' },
      { title: 'Feature Store', icon: Database, href: '/ai-ia-pro/ai-feature-store', featureId: 'copilot.feature_store' },
      { title: 'ML Experiment Tracker', icon: FlaskConical, href: '/ai-ia-pro/ai-experiment-tracker', featureId: 'copilot.ml_experiments' },
      { title: 'Scanner Shadow IT Data', icon: ScanSearch, href: '/ai-ia-pro/shadow-ai', featureId: 'copilot.shadow_it' },
      { title: 'Dashboard Systèmes IA', icon: LayoutDashboard, href: '/ai-ia-pro/ai-system-dashboard', featureId: 'copilot.ai_system_dashboard' },
      { title: 'Moniteur Drift Modèle', icon: TrendingDown, href: '/ai-ia-pro/model-drift-monitor', featureId: 'copilot.model_drift_monitor' },
      { title: 'Dashboard IA Responsable', icon: Shield, href: '/ai-ia-pro/responsible-ai-dash', featureId: 'copilot.responsible_ai_dash' },
    ],
  },
]

export const collaborateurCopilotNav: NavItem[] = [
  {
    title: 'AI Copilot — Mon Espace', icon: Sparkles,
    children: [
      { title: 'Mon Assistant Conformité', icon: Bot, href: '/workspace-pro/my-compliance-assistant', featureId: 'copilot.my_assistant' },
      { title: 'Priorisation Tâches IA', icon: ListOrdered, href: '/workspace-pro/smart-task-prioritizer', featureId: 'copilot.smart_tasks' },
      { title: 'Mon Tableau de Bord', icon: LayoutDashboard, href: '/workspace-pro/personal-dashboard', featureId: 'copilot.personal_dashboard' },
      { title: 'Mon Radar Risques', icon: Radar, href: '/workspace-pro/personal-risk-radar', featureId: 'copilot.personal_risk_radar' },
      { title: 'Hub de Connaissances', icon: BookOpen, href: '/workspace-pro/knowledge-hub', featureId: 'copilot.knowledge_hub' },
    ],
  },
]

// ─── Footer items (always shown) ────────────────────────────────────────────

export const footerNavItems: NavItem[] = [
  { title: 'Rapports', icon: BarChart3, href: '/reports' },
  { title: 'Intégrations', icon: Plug, href: '/integrations' },
  { title: 'Assistant IA', icon: MessageSquare, href: '/chat' },
]

// ─── Persona Context → Nav mapping ──────────────────────────────────────────
// Each persona sees a curated set of modules relevant to their role

/** Map of persona context ID → nav items for that persona */
export const NAV_REGISTRY: Record<ContextId, NavItem[]> = {

  // ── DIRIGEANT: Vision stratégique, KPIs, board ──
  'dirigeant': [
    ...strategyNav,
    ...governanceNav,
    ...risksNav,
    ...maturityNav,
    ...dashboardProNav,
    ...strategyProNav,
    ...businessModelProNav,
    ...orgContextNav,
    ...transformationProNav,
    ...esgNav,
    ...aiIntelligenceNav,
    ...evaluationNav,
    {
      title: 'Veille IA', icon: Radar,
      children: [
        { title: 'Digest de Veille', icon: Newspaper, href: '/reg-change/digests' },
      ],
    },
    ...dirigeantCopilotNav,
  ],

  // ── CISO: Sécurité, incidents, contrôles ──
  'ciso': [
    ...assessmentsNav,
    ...risksNav,
    ...complianceNav,
    ...frameworksNav,
    ...governanceNav,
    ...evidenceNav,
    ...securityProNav,
    ...questionnairesNav,
    ...riskProNav,
    ...riskIntelligenceNav,
    ...vendorProNav,
    ...caseMgmtNav,
    ...policyMgmtNav,
    ...evaluationNav,
    ...pdcaNav,
    ...cisoCopilotNav,
  ],

  // ── DPO: Vie privée, RGPD, consentements ──
  'dpo': [
    ...complianceNav,
    ...risksNav,
    ...assessmentsNav,
    ...governanceNav,
    ...frameworksNav,
    ...evidenceNav,
    ...complianceProNav,
    ...questionnairesNav,
    ...dataProNav,
    ...ethicsProNav,
    ...regChangeNav,
    ...regEngagementNav,
    ...policyMgmtNav,
    ...evaluationNav,
    ...dpoCopilotNav,
  ],

  // ── RISK MANAGER: Registre, analyse, traitement ──
  'risk-manager': [
    ...risksNav,
    ...assessmentsNav,
    ...complianceNav,
    ...frameworksNav,
    ...governanceNav,
    ...evidenceNav,
    ...maturityNav,
    ...riskProNav,
    ...riskIntelligenceNav,
    ...vendorProNav,
    ...caseMgmtNav,
    ...regChangeNav,
    ...evaluationNav,
    ...pdcaNav,
    ...riskManagerCopilotNav,
  ],

  // ── COMPLIANCE OFFICER: Multi-réglementaire, audits ──
  'compliance-officer': [
    ...complianceNav,
    ...risksNav,
    ...assessmentsNav,
    ...frameworksNav,
    ...governanceNav,
    ...evidenceNav,
    ...maturityNav,
    ...complianceProNav,
    ...questionnairesNav,
    ...auditProNav,
    ...ethicsProNav,
    ...vendorProNav,
    ...regChangeNav,
    ...regEngagementNav,
    ...policyMgmtNav,
    ...caseMgmtNav,
    ...esgNav,
    ...iso42001Nav,
    ...jurisdictionNav,
    ...connectedGrcNav,
    ...evaluationNav,
    ...pdcaNav,
    ...complianceOfficerCopilotNav,
  ],

  // ── DATA & IA OFFICER: Données, qualité, IA responsable ──
  'data-ia-officer': [
    ...assessmentsNav,
    ...complianceNav,
    ...risksNav,
    ...frameworksNav,
    ...governanceNav,
    ...evidenceNav,
    ...dataProNav,
    ...aiIaProNav,
    ...complianceProNav,
    ...maturityProNav,
    ...aiIntelligenceNav,
    ...iso42001Nav,
    ...jurisdictionNav,
    ...watchNav,
    ...orgContextNav,
    ...dataIaOfficerCopilotNav,
  ],

  // ── COLLABORATEUR: Mes tâches, évaluations, signalements ──
  'collaborateur': [
    ...assessmentsNav,
    ...complianceNav,
    ...frameworksNav,
    ...evidenceNav,
    ...maturityNav,
    ...workspaceProNav,
    ...caseMgmtNav,
    ...pdcaNav,
    ...policyMgmtNav,
    ...automationNav,
    ...collaborateurCopilotNav,
  ],

  // ── ADMINISTRATION: Équipe, prestataires, facturation ──
  'administration': [
    {
      title: 'Administration', icon: UserCog,
      children: [
        { title: 'Utilisateurs', icon: Users, href: '/admin/users' },
        { title: 'Rôles', icon: Lock, href: '/admin/roles' },
        { title: 'Organisation', icon: Building2, href: '/admin/organization' },
        { title: 'SSO', icon: Lock, href: '/admin/sso' },
        { title: 'Provisioning SCIM', icon: KeyRound, href: '/admin/scim-provisioning' },
        { title: 'Trust Center', icon: ShieldCheck, href: '/admin/trust-center' },
        { title: 'Certifications & Résidence', icon: Award, href: '/admin/certifications' },
        { title: 'Salle du Conseil', icon: Gavel, href: '/admin/board-room' },
        { title: 'Agents Persistants', icon: Bot, href: '/admin/agents' },
        { title: 'Control-Plane (FinOps)', icon: Activity, href: '/admin/control-plane' },
      ],
    },
    {
      title: 'Vie Privée', icon: ShieldCheck,
      children: [
        { title: 'Centre Vie Privée', icon: ShieldCheck, href: '/privacy' },
        { title: 'Consentement Cookies', icon: FileCheck, href: '/privacy/consent' },
        { title: 'Vos Droits', icon: Users, href: '/privacy/rights' },
        { title: 'Suppression Données', icon: AlertTriangle, href: '/privacy/deletion' },
        { title: 'Demande DSAR', icon: FileText, href: '/privacy/dsar' },
        { title: 'Admin Vie Privée', icon: Eye, href: '/privacy/admin' },
      ],
    },
    {
      title: 'Documents Juridiques', icon: Scale,
      children: [
        { title: 'Centre Juridique', icon: Scale, href: '/legal' },
        { title: 'Confidentialité', icon: Shield, href: '/legal/privacy' },
        { title: 'Conditions d\'Utilisation', icon: FileText, href: '/legal/terms' },
        { title: 'Cookies', icon: Globe, href: '/legal/cookies' },
        { title: 'Conformité RGPD', icon: Globe, href: '/legal/gdpr' },
        { title: 'Sécurité', icon: Lock, href: '/legal/security' },
      ],
    },
    {
      title: 'Auto-Gestion', icon: UserCog,
      children: [
        { title: 'Abonnement & Paiements', icon: CreditCard, href: '/self-manage/finances' },
      ],
    },
    ...connectedGrcNav,
    ...evaluationNav,
    ...automationNav,
  ],
}
